import { useCallback, useEffect, useRef } from 'react';
import { useBlocker } from '@tanstack/react-router';

export interface UnsavedChangesGuard {
  readonly dialogOpen: boolean;
  readonly proceed: () => void;
  readonly reset: () => void;
  readonly allowNextNavigation: () => void;
}

export interface UseUnsavedChangesGuardOpts {
  readonly dirty: boolean;
}

export function useUnsavedChangesGuard({
  dirty,
}: UseUnsavedChangesGuardOpts): UnsavedChangesGuard {
  const bypassRef = useRef(false);

  const blocker = useBlocker({
    shouldBlockFn: () => {
      if (bypassRef.current) {
        bypassRef.current = false;
        return false;
      }
      return dirty;
    },
    enableBeforeUnload: () => dirty && !bypassRef.current,
    withResolver: true,
  });

  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (bypassRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const allowNextNavigation = useCallback(() => {
    bypassRef.current = true;
  }, []);

  return {
    dialogOpen: blocker.status === 'blocked',
    proceed: blocker.status === 'blocked' ? blocker.proceed : noop,
    reset: blocker.status === 'blocked' ? blocker.reset : noop,
    allowNextNavigation,
  };
}

function noop() {}
